<script lang="ts">
    import { getContext } from 'svelte'
    import { derived as sderived } from 'svelte/store'

    import Icon from '@iconify/svelte'

    import type { HydriaDocument, IAttachment } from 'hydria/HydriaDocument'
    import { gettext as _, tgettext } from 'hydria/services/gettext'

    import Alignment from '../BaseFields/Alignment.svelte'
    import HtmlField from '../BaseFields/HtmlField.svelte'
    import SchemeSelection from '../BaseFields/Scheme.svelte'
    import ImageSelection from '../Widgets/ImageSelection.svelte'
    import type { IQuoteSection } from '../models'
    import { asAny } from 'hydria/svelte_helpers'

    interface Props {
        sectionEdit: IQuoteSection
        sectionSaved: IQuoteSection
        disabled: boolean
        set: (v) => void
    }

    let { sectionEdit, sectionSaved, disabled, set: doSet = (v) => {} }: Props = $props()

    function findStyle(source: string[], start: string) {
        const first = source.filter((s) => s.startsWith(`${start}-`))[0]
        return first != null ? first.slice(start.length + 1) : null
    }
    function setStyle(start: string, value: string) {
        const data = { styles: [...sectionEdit.styles.filter((s) => !s.startsWith(`${start}-`)), `${start}-${value}`] }
        data.styles.sort()
        doSet(data)
    }

    const Document = getContext('Document') as HydriaDocument
    const images = sderived(Document.attachedImages, ($atts) => $atts)

    let alignClass: string | null | undefined = $derived(sectionEdit != null ? findStyle(sectionEdit.styles, 'align') : null)
    let savedAlignClass: string | null | undefined = $derived(sectionSaved != null ? findStyle(sectionSaved.styles, 'align') : null)

    let quoteClass: string | null | undefined = $derived(sectionEdit != null ? findStyle(sectionEdit.styles, 'quote') : null)
    let savedQuoteClass: string | null | undefined = $derived(sectionSaved != null ? findStyle(sectionSaved.styles, 'quote') : null)

    let posClass: string | null | undefined = $derived(sectionEdit != null ? findStyle(sectionEdit.styles, 'pos') : null)
    let savedPosClass: string | null | undefined = $derived(sectionSaved != null ? findStyle(sectionSaved.styles, 'pos') : null)

    let schemeClass: string | null | undefined = $derived(sectionEdit != null ? findStyle(sectionEdit.styles, 'scheme') : null)
    let savedSchemeClass: string | null | undefined = $derived(sectionSaved != null ? findStyle(sectionSaved.styles, 'scheme') : null)

    // function bgImgStyle(attId: string, altName: string = null) {
    //     if (attId == null) return ''
    //     const att = $images.filter((img) => img.id === attId)[0]
    //     if (att == null) return ''
    //     if (!altName) return `background-image: url(${att.url});`
    //     const alts = att.alt_image_sizes.filter((alt) => alt.sizename == altName)
    //     if (!alts.length) return ''
    //     return `background-image: url(${alts[0].url});`
    // }
    function bgImageUrl(attId: string, altName: string | null = null) {
        if (attId == null) return ''
        const att = $images.filter((img) => img.id === attId)[0]
        if (att == null) return ''
        if (!altName) return att.url
        const alts = att.alt_image_sizes.filter((alt) => alt.sizename == altName)
        if (!alts.length) return ''
        return alts[0].url
    }

    let imageAttachment: IAttachment | null = $derived(sectionEdit && sectionEdit.image_id ? $images.filter((img) => img.id === sectionEdit.image_id)[0] : null)

    let editHtml: null | 'source_html' | 'content_html' = $state(null)
</script>

<div class="container mb-3">
    <div class="row">
        <div class="col">
            <SchemeSelection {disabled} value={schemeClass} saved={savedSchemeClass} save={(e) => setStyle('scheme', e)} />
        </div>
        <div class="col" style="max-width: 10rem;">
            <Alignment label={tgettext('Alignement')} {disabled} value={alignClass} saved={savedAlignClass} save={(e) => setStyle('align', e)} />
        </div>
        <div class="col" style="max-width: 11rem">
            <Alignment
                label={tgettext('Alignement de l’image')}
                {disabled}
                value={posClass}
                saved={savedPosClass}
                save={(e) => setStyle('pos', e)}
                allowed={['start', 'end']}
                aligns={[
                    { id: 'start', text: tgettext('Gauche'), icon: 'gridicons:align-image-left' },
                    { id: 'end', text: tgettext('Droit'), icon: 'gridicons:align-image-right' },
                ]}
            />
        </div>
        <div class="col" style="max-width: 13rem">
            <Alignment
                label={tgettext('Style')}
                {disabled}
                value={quoteClass}
                saved={savedQuoteClass}
                save={(e) => setStyle('quote', e)}
                allowed={null}
                grouped={false}
                aligns={[
                    { id: 'normal', text: tgettext('Citation'), icon: null, iconText: 'Normal' },
                    { id: 'simple', text: tgettext('Simple'), icon: null, iconText: 'Simple' },
                    { id: 'simple-big', text: tgettext('Simple, avec texte plus gros'), icon: null, iconText: 'Gros texte' },
                    { id: 'linecut', text: tgettext('Ligne de séparation'), icon: null, iconText: 'Ligne' },
                ]}
            />
        </div>
        <div class="col">
            <ImageSelection
                {disabled}
                value={sectionEdit.image_id}
                saved={sectionSaved ? sectionSaved.image_id : undefined}
                allowEmpty={true}
                label={tgettext('Image de citation')}
                previewName={'vignette'}
                {Document}
                save={(e) => {
                    doSet({ image_id: e })
                }}
            />
        </div>
        <div class="col" style="max-width: 9rem;">
            <ImageSelection
                {disabled}
                value={sectionEdit.bg_image_id}
                saved={sectionSaved ? sectionSaved.bg_image_id : undefined}
                allowEmpty={true}
                label={tgettext('Image de fond')}
                previewName={'vignette'}
                {Document}
                save={(e) => {
                    doSet({ bg_image_id: e })
                }}
            />
        </div>
    </div>
</div>

<div class="quote-preview overflow-hidden outer section-quote scheme {sectionEdit.styles.join(' ')}" class:has-image={imageAttachment} class:has-bg-image={sectionEdit.bg_image_id}>
    <div class="inner">
        {#if sectionEdit.bg_image_id}
            <div class="bg"><img src={bgImageUrl(sectionEdit.bg_image_id)} alt="" /></div>
        {/if}
        <div class="content container-xl">
            <div class="row">
                <div class="quote-layout">
                    {#if imageAttachment}
                        <div class="img">
                            <img src={imageAttachment.url} alt={imageAttachment.filename} width={asAny(imageAttachment.image_info).width} height={asAny(imageAttachment.image_info).height} />
                        </div>
                    {/if}
                    <div class="quote-left">&laquo;</div>
                    <div class="quote-right">&raquo;</div>
                    <div
                        class="text html-content selectable"
                        class:danger={sectionEdit.content_html == null || sectionEdit.content_html == ''}
                        aria-hidden="true"
                        ondblclick={() => {
                            if (!disabled) editHtml = 'content_html'
                        }}
                    >
                        <div class="edit-button">
                            <button
                                type="button"
                                class="btn btn-primary"
                                onclick={() => {
                                    if (!disabled) editHtml = 'content_html'
                                }}><Icon inline icon="mdi:pencil" /></button
                            >
                        </div>
                        {#if sectionEdit.content_html != null && sectionEdit.content_html != ''}
                            <div class="has-content">
                                {@html sectionEdit.content_html}
                            </div>
                        {:else}
                            <div class="no-content">
                                <div>{$_('Aucun texte')}</div>
                            </div>
                        {/if}
                    </div>
                    <div
                        class="source selectable"
                        aria-hidden="true"
                        ondblclick={() => {
                            if (!disabled) editHtml = 'source_html'
                        }}
                    >
                        <div class="edit-button">
                            <button
                                type="button"
                                class="btn btn-primary"
                                onclick={() => {
                                    if (!disabled) editHtml = 'source_html'
                                }}><Icon inline icon="mdi:pencil" /></button
                            >
                        </div>
                        {#if sectionEdit.source_html}
                            <div class="has-source">{@html sectionEdit.source_html}</div>
                        {:else}
                            <div class="no-source">{$_('Auteur')}</div>
                        {/if}
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>

{#if editHtml != null}
    <HtmlField
        value={sectionEdit[editHtml]}
        saved={sectionSaved == null ? undefined : sectionSaved[editHtml]}
        label={editHtml == 'source_html' ? tgettext('Auteur') : tgettext('Citation')}
        isValid={(v) => editHtml == 'source_html' || !!(v && v.trim())}
        {Document}
        close={() => (editHtml = null)}
        save={(e) => {
            const v = {}
            v[asAny(editHtml)] = e
            doSet(v)
            editHtml = null
        }}
    />
{/if}
